import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../stores/store';
import { format } from 'date-fns';
import {
    Chip, CircularProgress, Card, CardContent,
    Typography, Alert, Dialog, DialogTitle, DialogContent,
    DialogActions, Button, Box, Divider, Tabs, Tab,
    Tooltip, IconButton, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import HistoryIcon from '@mui/icons-material/History';
import InfoIcon from '@mui/icons-material/Info';
import BuildIcon from '@mui/icons-material/Build';
import CategoryIcon from '@mui/icons-material/Category';
import ListAltIcon from '@mui/icons-material/ListAlt';
import FeaturedPlayListIcon from '@mui/icons-material/FeaturedPlayList';
import DataTable from '../../Generic/DataTable';
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import useLocalizedTextsMap from '../../../hooks/useLocalizedTextsMap';
import { useSetMenu } from "../../../hooks/useSetMenu";
import { useSnackbar } from 'notistack';

const MyRequests = () => {
    const axiosPrivate = useAxiosPrivate();
    const { mainStore } = useStore();
    const { t } = useTranslation();
    const localizedTextsMap = useLocalizedTextsMap();
    const { enqueueSnackbar } = useSnackbar();

    // Use a dedicated menu number for MyRequests
    useSetMenu(mainStore.MyRequestsMenuType, mainStore.MyRequestsMenuNo);

    const [pageSize, setPageSize] = useState(10);
    const [loading, setLoading] = useState(true);
    const [requests, setRequests] = useState([]);
    const [contentTypeFilter, setContentTypeFilter] = useState('all');

    // Detail dialog state
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [detailTab, setDetailTab] = useState(0);

    // Content type constants
    const CONTENT_TYPES = {
        TOOL_PASSPORT: 'ToolPassport',
        CRITERIA: 'Criteria',
        CRITERIA_VALUE: 'CriteriaValue',
        TOOL_FEATURE: 'ToolFeature'
    };

    // Fetch user's all requests from all content types
    const fetchRequests = useCallback(async () => {
        setLoading(true);
        try {
            // Fetch requests from all endpoints in parallel
            const [toolPassportRes, criteriaRes, criteriaValueRes, toolFeatureRes] = await Promise.all([
                axiosPrivate.get('/ToolPassports/MyTools/AllRequests').catch(() => ({ data: [] })),
                axiosPrivate.get('/Criterias/MyCriteria/AllRequests').catch(() => ({ data: [] })),
                axiosPrivate.get('/CriteriaValues/MyCriteriaValues/AllRequests').catch(() => ({ data: [] })),
                axiosPrivate.get('/ToolFeatures/MyToolFeatures/AllRequests').catch(() => ({ data: [] }))
            ]);

            // Add content type to each request and combine
            const toolPassportRequests = (toolPassportRes.data || []).map(r => ({
                ...r,
                contentType: CONTENT_TYPES.TOOL_PASSPORT,
                itemName: r.originalToolName || r.name
            }));
            const criteriaRequests = (criteriaRes.data || []).map(r => ({
                ...r,
                contentType: CONTENT_TYPES.CRITERIA,
                itemName: r.originalName || r.name
            }));
            const criteriaValueRequests = (criteriaValueRes.data || []).map(r => ({
                ...r,
                contentType: CONTENT_TYPES.CRITERIA_VALUE,
                itemName: r.originalValue || r.value
            }));
            const toolFeatureRequests = (toolFeatureRes.data || []).map(r => ({
                ...r,
                contentType: CONTENT_TYPES.TOOL_FEATURE,
                itemName: r.originalToolPassportName || '-'
            }));

            // Combine all requests and sort by date (newest first)
            const allRequests = [
                ...toolPassportRequests,
                ...criteriaRequests,
                ...criteriaValueRequests,
                ...toolFeatureRequests
            ].sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));

            // Generate unique IDs for the combined list
            const requestsWithUniqueIds = allRequests.map((r, index) => ({
                ...r,
                uniqueId: `${r.contentType}-${r.id}-${index}`
            }));

            setRequests(requestsWithUniqueIds);
        } catch (error) {
            console.error('Error fetching requests:', error);
            enqueueSnackbar(t('errorMessageError'), { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [axiosPrivate, enqueueSnackbar, t]);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    // Handle view details
    const handleViewDetails = (request) => {
        setSelectedRequest(request);
        setDetailTab(0);
        setDetailDialogOpen(true);
    };

    // Helper to check if a field has changed
    const hasFieldChanged = (originalValue, proposedValue) => {
        if (!proposedValue) return false;
        return proposedValue !== originalValue;
    };

    // Helper to render HTML content as text (strip tags)
    const stripHtml = (html) => {
        if (!html) return '-';
        const stripped = html.replace(/<[^>]*>/g, '');
        return stripped.length > 150 ? stripped.substring(0, 150) + '...' : stripped;
    };

    // Render comparison row for changes
    const renderChangeRow = (label, originalValue, proposedValue, isHtml = false) => {
        const changed = hasFieldChanged(originalValue, proposedValue);
        if (!changed && !proposedValue) return null;

        return (
            <tr key={label}>
                <td style={labelCellStyle}>{label}</td>
                <td style={valueCellStyle}>
                    {isHtml ? stripHtml(originalValue) : (originalValue || '-')}
                </td>
                <td style={{
                    ...valueCellStyle,
                    background: changed ? '#fff3e0' : 'inherit',
                    color: changed ? '#e65100' : 'inherit',
                    fontWeight: changed ? 500 : 'normal'
                }}>
                    {isHtml ? stripHtml(proposedValue) : (proposedValue || '-')}
                    {changed && (
                        <Chip
                            size="small"
                            label={t('changed')}
                            color="warning"
                            sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                        />
                    )}
                </td>
            </tr>
        );
    };

    // Get status chip
    const getStatusChip = (status) => {
        switch (status) {
            case 'Pending':
                return <Chip size="small" color="warning" icon={<PendingActionsIcon />} label={t('statusPending')} />;
            case 'Approved':
                return <Chip size="small" color="success" icon={<CheckCircleIcon />} label={t('statusApproved')} />;
            case 'Rejected':
                return <Chip size="small" color="error" icon={<CancelIcon />} label={t('statusRejected')} />;
            default:
                return <Chip size="small" label={status} />;
        }
    };

    // Get request type chip
    const getRequestTypeChip = (type) => {
        if (type === 'Edit') {
            return <Chip size="small" color="info" icon={<EditIcon style={{ fontSize: 14 }} />} label={t('editRequest')} />;
        }
        return <Chip size="small" color="error" icon={<DeleteIcon style={{ fontSize: 14 }} />} label={t('deleteRequest')} />;
    };

    // Get content type chip
    const getContentTypeChip = (contentType) => {
        switch (contentType) {
            case CONTENT_TYPES.TOOL_PASSPORT:
                return <Chip size="small" variant="outlined" icon={<BuildIcon style={{ fontSize: 14 }} />} label={t('toolPassport')} />;
            case CONTENT_TYPES.CRITERIA:
                return <Chip size="small" variant="outlined" color="secondary" icon={<CategoryIcon style={{ fontSize: 14 }} />} label={t('criteria')} />;
            case CONTENT_TYPES.CRITERIA_VALUE:
                return <Chip size="small" variant="outlined" color="info" icon={<ListAltIcon style={{ fontSize: 14 }} />} label={t('criteriaValue')} />;
            case CONTENT_TYPES.TOOL_FEATURE:
                return <Chip size="small" variant="outlined" color="warning" icon={<FeaturedPlayListIcon style={{ fontSize: 14 }} />} label={t('toolFeature')} />;
            default:
                return <Chip size="small" variant="outlined" label={contentType} />;
        }
    };

    // Filter requests based on content type
    const filteredRequests = useMemo(() => {
        if (contentTypeFilter === 'all') return requests;
        return requests.filter(r => r.contentType === contentTypeFilter);
    }, [requests, contentTypeFilter]);

    // Calculate counts
    const counts = useMemo(() => {
        return {
            total: filteredRequests.length,
            pending: filteredRequests.filter(r => r.status === 'Pending').length,
            approved: filteredRequests.filter(r => r.status === 'Approved').length,
            rejected: filteredRequests.filter(r => r.status === 'Rejected').length
        };
    }, [filteredRequests]);

    // Content type counts for filter buttons
    const contentTypeCounts = useMemo(() => {
        return {
            all: requests.length,
            [CONTENT_TYPES.TOOL_PASSPORT]: requests.filter(r => r.contentType === CONTENT_TYPES.TOOL_PASSPORT).length,
            [CONTENT_TYPES.CRITERIA]: requests.filter(r => r.contentType === CONTENT_TYPES.CRITERIA).length,
            [CONTENT_TYPES.CRITERIA_VALUE]: requests.filter(r => r.contentType === CONTENT_TYPES.CRITERIA_VALUE).length,
            [CONTENT_TYPES.TOOL_FEATURE]: requests.filter(r => r.contentType === CONTENT_TYPES.TOOL_FEATURE).length
        };
    }, [requests]);

    // Columns definition
    const columns = useMemo(() => [
        {
            field: 'contentType',
            headerName: t('contentType'),
            flex: 0.6,
            minWidth: 130,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            headerAlign: 'center',
            align: 'center',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => getContentTypeChip(params.value),
        },
        {
            field: 'requestType',
            headerName: t('requestType'),
            flex: 0.5,
            minWidth: 120,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            headerAlign: 'center',
            align: 'center',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => getRequestTypeChip(params.value),
        },
        {
            field: 'itemName',
            headerName: t('itemName'),
            flex: 1,
            minWidth: 150,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => params.value || '-',
        },
        {
            field: 'status',
            headerName: t('status'),
            flex: 0.5,
            minWidth: 120,
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => getStatusChip(params.value),
        },
        {
            field: 'createdDate',
            headerName: t('submittedDate'),
            flex: 0.6,
            minWidth: 130,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            valueFormatter: (params) => {
                if (params) {
                    return format(new Date(params), "dd/MM/yyyy HH:mm");
                }
                return "-";
            },
        },
        {
            field: 'reviewDate',
            headerName: t('reviewDate'),
            flex: 0.6,
            minWidth: 130,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            valueFormatter: (params) => {
                if (params) {
                    return format(new Date(params), "dd/MM/yyyy HH:mm");
                }
                return "-";
            },
        },
        {
            field: 'reviewComments',
            headerName: t('reviewComments'),
            flex: 1,
            minWidth: 150,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => {
                const comment = params.value;
                if (!comment) return '-';
                // Truncate long comments
                return comment.length > 50 ? comment.substring(0, 50) + '...' : comment;
            },
        },
        {
            field: 'actions',
            headerName: t('action'),
            flex: 0.5,
            minWidth: 100,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => (
                <Tooltip title={t('details')}>
                    <IconButton
                        size="small"
                        color="info"
                        onClick={() => handleViewDetails(params.row)}
                        sx={{
                            border: '1px solid',
                            borderColor: 'info.main',
                            borderRadius: 1,
                            px: 1.5,
                            py: 0.5,
                            '&:hover': {
                                backgroundColor: 'info.light',
                            }
                        }}
                    >
                        <InfoIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            ),
        },
    ], [t]);

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">{t("myRequestsTitle")}</h4>
            </div>

            {/* Info Alert */}
            <Alert severity="info" className="mb-3" icon={<HistoryIcon />}>
                {t("myRequestsInfo")}
            </Alert>

            {/* Content Type Filter */}
            <Box sx={{ mb: 3 }}>
                <ToggleButtonGroup
                    value={contentTypeFilter}
                    exclusive
                    onChange={(e, newFilter) => newFilter && setContentTypeFilter(newFilter)}
                    aria-label="content type filter"
                    size="small"
                >
                    <ToggleButton value="all">
                        {t('all')} ({contentTypeCounts.all})
                    </ToggleButton>
                    <ToggleButton value={CONTENT_TYPES.TOOL_PASSPORT}>
                        <BuildIcon sx={{ mr: 0.5, fontSize: 16 }} />
                        {t('toolPassport')} ({contentTypeCounts[CONTENT_TYPES.TOOL_PASSPORT]})
                    </ToggleButton>
                    <ToggleButton value={CONTENT_TYPES.CRITERIA}>
                        <CategoryIcon sx={{ mr: 0.5, fontSize: 16 }} />
                        {t('criteria')} ({contentTypeCounts[CONTENT_TYPES.CRITERIA]})
                    </ToggleButton>
                    <ToggleButton value={CONTENT_TYPES.CRITERIA_VALUE}>
                        <ListAltIcon sx={{ mr: 0.5, fontSize: 16 }} />
                        {t('criteriaValue')} ({contentTypeCounts[CONTENT_TYPES.CRITERIA_VALUE]})
                    </ToggleButton>
                    <ToggleButton value={CONTENT_TYPES.TOOL_FEATURE}>
                        <FeaturedPlayListIcon sx={{ mr: 0.5, fontSize: 16 }} />
                        {t('toolFeature')} ({contentTypeCounts[CONTENT_TYPES.TOOL_FEATURE]})
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* Summary Cards */}
            <div className="row mb-4">
                <div className="col-md-3 col-sm-6 mb-2">
                    <Card>
                        <CardContent sx={{ py: 2 }}>
                            <Typography variant="h4" component="div" color="primary.main">
                                {counts.total}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('totalRequests')}
                            </Typography>
                        </CardContent>
                    </Card>
                </div>
                <div className="col-md-3 col-sm-6 mb-2">
                    <Card sx={{ borderLeft: 4, borderColor: 'warning.main' }}>
                        <CardContent sx={{ py: 2 }}>
                            <Typography variant="h4" component="div" color="warning.main">
                                {counts.pending}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('statusPending')}
                            </Typography>
                        </CardContent>
                    </Card>
                </div>
                <div className="col-md-3 col-sm-6 mb-2">
                    <Card sx={{ borderLeft: 4, borderColor: 'success.main' }}>
                        <CardContent sx={{ py: 2 }}>
                            <Typography variant="h4" component="div" color="success.main">
                                {counts.approved}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('statusApproved')}
                            </Typography>
                        </CardContent>
                    </Card>
                </div>
                <div className="col-md-3 col-sm-6 mb-2">
                    <Card sx={{ borderLeft: 4, borderColor: 'error.main' }}>
                        <CardContent sx={{ py: 2 }}>
                            <Typography variant="h4" component="div" color="error.main">
                                {counts.rejected}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('statusRejected')}
                            </Typography>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Data Table */}
            {loading ? (
                <div className="d-flex justify-content-center p-5">
                    <CircularProgress />
                </div>
            ) : filteredRequests.length === 0 ? (
                <div className="text-center p-5">
                    <HistoryIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="textSecondary">
                        {t('noRequestsYet')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {t('noRequestsDescription')}
                    </Typography>
                </div>
            ) : (
                <DataTable
                    rows={filteredRequests}
                    columns={columns}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    localeTextsMap={localizedTextsMap}
                    getRowId={(row) => row.uniqueId}
                />
            )}

            {/* Detail Dialog */}
            <Dialog
                open={detailDialogOpen}
                onClose={() => setDetailDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HistoryIcon color="primary" />
                    {t('requestDetails')}
                </DialogTitle>
                <DialogContent>
                    {selectedRequest && (
                        <Box>
                            {/* Status Alert */}
                            <Alert
                                severity={
                                    selectedRequest.status === 'Approved' ? 'success' :
                                    selectedRequest.status === 'Rejected' ? 'error' : 'warning'
                                }
                                sx={{ mb: 2 }}
                            >
                                <strong>{t('status')}: </strong>
                                {selectedRequest.status === 'Pending' && t('statusPending')}
                                {selectedRequest.status === 'Approved' && t('statusApproved')}
                                {selectedRequest.status === 'Rejected' && t('statusRejected')}
                            </Alert>

                            {/* Tabs for Edit requests */}
                            {selectedRequest.requestType === 'Edit' && (
                                <Tabs
                                    value={detailTab}
                                    onChange={(e, newValue) => setDetailTab(newValue)}
                                    sx={{ mb: 2 }}
                                >
                                    <Tab icon={<InfoIcon />} iconPosition="start" label={t('requestDetails')} />
                                    <Tab icon={<CompareArrowsIcon />} iconPosition="start" label={t('viewChanges')} />
                                </Tabs>
                            )}

                            {/* Tab 0: Request Info */}
                            {(detailTab === 0 || selectedRequest.requestType === 'Delete') && (
                                <>
                                    {/* Request Info Table */}
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <tbody>
                                            <tr>
                                                <td style={labelCellStyle}>{t('contentType')}</td>
                                                <td style={valueCellStyle}>{getContentTypeChip(selectedRequest.contentType)}</td>
                                            </tr>
                                            <tr>
                                                <td style={labelCellStyle}>{t('requestType')}</td>
                                                <td style={valueCellStyle}>{getRequestTypeChip(selectedRequest.requestType)}</td>
                                            </tr>
                                            {/* Content-specific item name row */}
                                            {selectedRequest.contentType === CONTENT_TYPES.TOOL_PASSPORT && (
                                                <tr>
                                                    <td style={labelCellStyle}>{t('originalTool')}</td>
                                                    <td style={valueCellStyle}>{selectedRequest.originalToolName}</td>
                                                </tr>
                                            )}
                                            {selectedRequest.contentType === CONTENT_TYPES.CRITERIA && (
                                                <tr>
                                                    <td style={labelCellStyle}>{t('originalCriteria')}</td>
                                                    <td style={valueCellStyle}>{selectedRequest.originalName}</td>
                                                </tr>
                                            )}
                                            {selectedRequest.contentType === CONTENT_TYPES.CRITERIA_VALUE && (
                                                <tr>
                                                    <td style={labelCellStyle}>{t('originalCriteriaValue')}</td>
                                                    <td style={valueCellStyle}>{selectedRequest.originalValue}</td>
                                                </tr>
                                            )}
                                            {selectedRequest.contentType === CONTENT_TYPES.TOOL_FEATURE && (
                                                <>
                                                    <tr>
                                                        <td style={labelCellStyle}>{t('toolPassportName')}</td>
                                                        <td style={valueCellStyle}>{selectedRequest.originalToolPassportName}</td>
                                                    </tr>
                                                    <tr>
                                                        <td style={labelCellStyle}>{t('criteria')}</td>
                                                        <td style={valueCellStyle}>{selectedRequest.originalCriteriaName}</td>
                                                    </tr>
                                                    <tr>
                                                        <td style={labelCellStyle}>{t('criteriaValue')}</td>
                                                        <td style={valueCellStyle}>{selectedRequest.originalCriteriaValueName}</td>
                                                    </tr>
                                                </>
                                            )}
                                            {/* Proposed name/value if applicable */}
                                            {selectedRequest.name && selectedRequest.contentType === CONTENT_TYPES.TOOL_PASSPORT && (
                                                <tr>
                                                    <td style={labelCellStyle}>{t('proposedName')}</td>
                                                    <td style={valueCellStyle}>{selectedRequest.name}</td>
                                                </tr>
                                            )}
                                            {selectedRequest.name && selectedRequest.contentType === CONTENT_TYPES.CRITERIA && (
                                                <tr>
                                                    <td style={labelCellStyle}>{t('proposedName')}</td>
                                                    <td style={valueCellStyle}>{selectedRequest.name}</td>
                                                </tr>
                                            )}
                                            {selectedRequest.value && selectedRequest.contentType === CONTENT_TYPES.CRITERIA_VALUE && (
                                                <tr>
                                                    <td style={labelCellStyle}>{t('proposedValue')}</td>
                                                    <td style={valueCellStyle}>{selectedRequest.value}</td>
                                                </tr>
                                            )}
                                            {selectedRequest.reason && (
                                                <tr>
                                                    <td style={labelCellStyle}>{t('reason')}</td>
                                                    <td style={valueCellStyle}>{selectedRequest.reason}</td>
                                                </tr>
                                            )}
                                            <tr>
                                                <td style={labelCellStyle}>{t('submittedDate')}</td>
                                                <td style={valueCellStyle}>
                                                    {selectedRequest.createdDate
                                                        ? format(new Date(selectedRequest.createdDate), "dd/MM/yyyy HH:mm")
                                                        : '-'}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>

                                    {/* Review Info (if reviewed) */}
                                    {(selectedRequest.status === 'Approved' || selectedRequest.status === 'Rejected') && (
                                        <>
                                            <Divider sx={{ my: 2 }} />
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                {t('reviewInfo')}
                                            </Typography>
                                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                <tbody>
                                                    {(selectedRequest.reviewedByFullName || selectedRequest.reviewedBy) && (
                                                        <tr>
                                                            <td style={labelCellStyle}>{t('reviewedBy')}</td>
                                                            <td style={valueCellStyle}>
                                                                {selectedRequest.reviewedByFullName || selectedRequest.reviewedBy}
                                                            </td>
                                                        </tr>
                                                    )}
                                                    {selectedRequest.reviewDate && (
                                                        <tr>
                                                            <td style={labelCellStyle}>{t('reviewDate')}</td>
                                                            <td style={valueCellStyle}>
                                                                {format(new Date(selectedRequest.reviewDate), "dd/MM/yyyy HH:mm")}
                                                            </td>
                                                        </tr>
                                                    )}
                                                    {selectedRequest.reviewComments && (
                                                        <tr>
                                                            <td style={labelCellStyle}>{t('reviewComments')}</td>
                                                            <td style={{
                                                                ...valueCellStyle,
                                                                color: selectedRequest.status === 'Rejected' ? '#d32f2f' : 'inherit',
                                                                fontWeight: selectedRequest.status === 'Rejected' ? 500 : 'normal'
                                                            }}>
                                                                {selectedRequest.reviewComments}
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </>
                                    )}
                                </>
                            )}

                            {/* Tab 1: View Changes (only for Edit requests) */}
                            {detailTab === 1 && selectedRequest.requestType === 'Edit' && (
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                                        {t('proposedChanges')}
                                    </Typography>
                                    <div style={{ overflowX: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '500px' }}>
                                            <thead>
                                                <tr style={{ background: '#f5f5f5' }}>
                                                    <th style={comparisonHeaderStyle}>{t('field')}</th>
                                                    <th style={comparisonHeaderStyle}>{t('originalValues')}</th>
                                                    <th style={comparisonHeaderStyle}>{t('proposedChanges')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {/* ToolPassport changes */}
                                                {selectedRequest.contentType === CONTENT_TYPES.TOOL_PASSPORT && (
                                                    <>
                                                        {renderChangeRow(
                                                            t('toolPassportName'),
                                                            selectedRequest.originalToolName,
                                                            selectedRequest.name
                                                        )}
                                                        {renderChangeRow(
                                                            t('toolPassportToolURL'),
                                                            selectedRequest.originalToolURL,
                                                            selectedRequest.toolURL
                                                        )}
                                                        {renderChangeRow(
                                                            t('toolPassportFunctions'),
                                                            selectedRequest.originalFunction,
                                                            selectedRequest.function,
                                                            true
                                                        )}
                                                        {renderChangeRow(
                                                            t('toolPassportAbstractionLevel'),
                                                            selectedRequest.originalAbstractionLevel,
                                                            selectedRequest.abstractionLevel,
                                                            true
                                                        )}
                                                        {renderChangeRow(
                                                            t('toolPassportIOConnection'),
                                                            selectedRequest.originalIOConnection,
                                                            selectedRequest.ioConnection,
                                                            true
                                                        )}
                                                        {renderChangeRow(
                                                            t('toolPassportVerificationTool'),
                                                            selectedRequest.originalVerificationTool,
                                                            selectedRequest.verificationTool,
                                                            true
                                                        )}
                                                        {renderChangeRow(
                                                            t('toolPassportApplicationAreaOrEquivalenceCoverage'),
                                                            selectedRequest.originalApplicationAreaOrEquivalenceCoverage,
                                                            selectedRequest.applicationAreaOrEquivalenceCoverage,
                                                            true
                                                        )}
                                                        {renderChangeRow(
                                                            t('toolPassportQualitativeFeature'),
                                                            selectedRequest.originalQualitativeFeature,
                                                            selectedRequest.qualitativeFeature,
                                                            true
                                                        )}
                                                        {renderChangeRow(
                                                            t('toolPassportQuantitativeFeature'),
                                                            selectedRequest.originalQuantitativeFeature,
                                                            selectedRequest.quantitativeFeature,
                                                            true
                                                        )}
                                                        {(selectedRequest.originalLogo || selectedRequest.newLogo) && (
                                                            <tr>
                                                                <td style={labelCellStyle}>{t('logo')}</td>
                                                                <td style={valueCellStyle}>
                                                                    {selectedRequest.originalLogo && (
                                                                        <img
                                                                            src={`${process.env.REACT_APP_SERVER_URL}/${selectedRequest.originalLogo.replace(/^\/+/, '')}`}
                                                                            alt="Original Logo"
                                                                            style={{ maxHeight: '50px', maxWidth: '80px' }}
                                                                            onError={(e) => { e.target.style.display = 'none'; }}
                                                                        />
                                                                    )}
                                                                </td>
                                                                <td style={{
                                                                    ...valueCellStyle,
                                                                    background: selectedRequest.newLogo && selectedRequest.newLogo !== selectedRequest.originalLogo ? '#fff3e0' : 'inherit'
                                                                }}>
                                                                    {selectedRequest.newLogo && (
                                                                        <img
                                                                            src={`${process.env.REACT_APP_SERVER_URL}/${selectedRequest.newLogo.replace(/^\/+/, '')}`}
                                                                            alt="New Logo"
                                                                            style={{ maxHeight: '50px', maxWidth: '80px' }}
                                                                            onError={(e) => { e.target.style.display = 'none'; }}
                                                                        />
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </>
                                                )}
                                                {/* Criteria changes */}
                                                {selectedRequest.contentType === CONTENT_TYPES.CRITERIA && (
                                                    <>
                                                        {renderChangeRow(
                                                            t('criteriaName'),
                                                            selectedRequest.originalName,
                                                            selectedRequest.name
                                                        )}
                                                        {renderChangeRow(
                                                            t('weightCoefficient'),
                                                            selectedRequest.originalWeightCoefficient?.toString(),
                                                            selectedRequest.weightCoefficient?.toString()
                                                        )}
                                                        {renderChangeRow(
                                                            t('isMandatory'),
                                                            selectedRequest.originalIsMandatory != null ? (selectedRequest.originalIsMandatory ? t('yes') : t('no')) : null,
                                                            selectedRequest.isMandatory != null ? (selectedRequest.isMandatory ? t('yes') : t('no')) : null
                                                        )}
                                                    </>
                                                )}
                                                {/* CriteriaValue changes */}
                                                {selectedRequest.contentType === CONTENT_TYPES.CRITERIA_VALUE && (
                                                    <>
                                                        {renderChangeRow(
                                                            t('criteriaValueName'),
                                                            selectedRequest.originalValue,
                                                            selectedRequest.value
                                                        )}
                                                        {renderChangeRow(
                                                            t('criteria'),
                                                            selectedRequest.originalCriteriaName,
                                                            selectedRequest.criteriaName
                                                        )}
                                                    </>
                                                )}
                                                {/* ToolFeature changes */}
                                                {selectedRequest.contentType === CONTENT_TYPES.TOOL_FEATURE && (
                                                    <>
                                                        {renderChangeRow(
                                                            t('toolPassportName'),
                                                            selectedRequest.originalToolPassportName,
                                                            selectedRequest.toolPassportName
                                                        )}
                                                        {renderChangeRow(
                                                            t('criteria'),
                                                            selectedRequest.originalCriteriaName,
                                                            selectedRequest.criteriaName
                                                        )}
                                                        {renderChangeRow(
                                                            t('criteriaValue'),
                                                            selectedRequest.originalCriteriaValueName,
                                                            selectedRequest.criteriaValueName
                                                        )}
                                                    </>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailDialogOpen(false)} variant="outlined">
                        {t('Close')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

// Table cell styles
const labelCellStyle = {
    padding: '10px 12px',
    borderBottom: '1px solid #e0e0e0',
    width: '35%',
    fontWeight: 600,
    color: '#374151',
    background: '#f8fafc'
};

const valueCellStyle = {
    padding: '10px 12px',
    borderBottom: '1px solid #e0e0e0',
    color: '#1e293b'
};

const comparisonHeaderStyle = {
    padding: '10px 12px',
    borderBottom: '2px solid #e0e0e0',
    textAlign: 'left',
    fontWeight: 600
};

export default observer(MyRequests);
